import React, { useState, useEffect } from 'react';
import {
    Backdrop,
    Card,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    ListItemText,
    OutlinedInput,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CustomFilter from 'src/components/filter/CustomFilter';
import Iconify from 'src/components/iconify';
import ClearButton from 'src/components/shared/ClearButton';
import DynamicTable from 'src/components/shared/DynamicTable';
import FilterButton from 'src/components/shared/FilterButton';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import Services from 'src/services/Services';
import moment from 'moment';
import { toast } from 'react-toastify';

export default function TaxFreeReportList({ parsedUser, tabs }) {
    const [reportList, setReportList] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterModal, setFilterModal] = useState(false);
    const [filters, setFilters] = useState({});
    const isMobile = useMediaQuery('(max-width:768px)');
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formData, setFormData] = useState({});

    const formatDate = (timestampOrMoment) => {
        if (moment.isMoment(timestampOrMoment)) {
            return timestampOrMoment.format("DD.MM.YYYY");
        }
        if (timestampOrMoment == null && timestampOrMoment !== 0) return "-";
        const date = new Date(timestampOrMoment * 1000);
        const year = date.getFullYear();
        if (isNaN(year) || year === 1970) return "-";
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}.${month}.${year}`;
    };

    const TABLE_HEAD = [
        { id: 'brand', label: "Marka" },
        { id: 'operationDate', label: "İşlem Tarihi", format: (value) => formatDate(value) },
        { id: 'customerInvoiceNo', label: "Müşteri Fatura No" },
        { id: 'customerName', label: "Müşteri İsmi" },
        { id: 'country', label: "Ülke" },
        { id: 'price', label: "Fatura Tutarı" },
        { id: 'returnPrice', label: "Ödenen İade Tutarı" },
        { id: 'paymentType', label: "Ödeme Türü" },
        { id: 'store', label: "Mağaza" },
        { id: 'poiTitle', label: "Lokasyon" },
        { id: 'staff', label: "EP Çalışanı" },
        { id: 'storeLocation', label: "Mağaza Lokasyonu" },
        { id: 'additionalNote', label: "Not", format: (value) => value ? value : "-"  },
        {
            id: 'action',
            label: '',
            alignRight: false,
            format: (value, row) => (
                <Tooltip title={"Detay"}>
                    <Iconify
                        style={{ color: '#98A2B3', width: 22, height: 22, cursor: 'pointer' }}
                        icon="bx:detail"
                        onClick={() => handleOpenDetail(row)}
                    />
                </Tooltip>
            ),
        }
    ];

    // === Yardımcı: benzersiz değerler ===
    const uniqueValues = (key) =>
        Array.from(new Set((originalData || []).map(x => x?.[key]).filter(Boolean)))
            .sort((a, b) => String(a).localeCompare(String(b), "tr-TR"));

    // === YENİ: Tek yerden filtre uygulayan yardımcı ===
    const applyFilters = (data, currentFilters) => {
        if (!currentFilters || Object.keys(currentFilters).length === 0) return data;

        const asJsDate = (val, type = "start") => {
            if (!val) return null;
            if (moment.isMoment(val)) {
                return (type === "start" ? val.startOf("day") : val.endOf("day")).toDate();
            }
            if (val instanceof Date) return val;
            return new Date(val);
        };

        return (data || []).filter((item) => {
            return Object.entries(currentFilters).every(([key, filterValue]) => {
                // Tarih
                if (key === 'operationDate') {
                    const itemDate = item[key] ? new Date(item[key] * 1000) : null;
                    const start = asJsDate(filterValue?.start, "start");
                    const end = asJsDate(filterValue?.end, "end");
                    if ((start || end) && !itemDate) return false;
                    if (start && itemDate && itemDate < start) return false;
                    if (end && itemDate && itemDate > end) return false;
                    return true;
                }

                // Çoklu seçim (brand, poiTitle vb.)
                if (Array.isArray(filterValue)) {
                    if (!filterValue.length) return true;
                    const raw = item[key];
                    const val = typeof raw === "string" ? raw : String(raw ?? "");
                    return filterValue.map(String).includes(val);
                }

                // Metin arama
                if (!filterValue || (typeof filterValue === "string" && filterValue.trim() === "")) return true;

                let itemValue = item[key];
                if (typeof itemValue === "number") itemValue = String(itemValue);

                const columnDef = TABLE_HEAD.find(col => col.id === key);
                if (columnDef?.format) {
                    try { itemValue = columnDef.format(itemValue, item); } catch {}
                }

                return (
                    itemValue &&
                    String(itemValue).toLocaleUpperCase("tr-TR")
                        .includes(String(filterValue).toLocaleUpperCase("tr-TR"))
                );
            });
        });
    };

    useEffect(() => {
        getReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // === GÜNCEL: preserveFilters parametresi ===
    const getReports = (preserveFilters = false) => {
        setLoading(true);
        Services.GetOperationReport(parsedUser.token, "tax-free")
            .then((res) => {
                if (res.data.status) {
                    const incoming = res.data.result || [];
                    setOriginalData(incoming);

                    const nextList =
                        preserveFilters && Object.keys(filters || {}).length > 0
                            ? applyFilters(incoming, filters)
                            : incoming;

                    setReportList(nextList);
                } else {
                    toast.error("Rapor bulunamadı.");
                    setReportList([]);
                    setOriginalData([]);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const handleModal = () => setFilterModal(true);
    const handleCloseModal = () => setFilterModal(false);

    // === GÜNCEL: Ortak helper ile filtrele ===
    const getFilteredTaxFreeReports = () => {
        const filteredData = applyFilters(originalData, filters);
        setReportList(filteredData);
        handleCloseModal();
    };

    const clearFilters = () => {
        setFilters({});
        setReportList(originalData);
    };

    const handleOpenDetail = (row) => {
        setSelectedRow(row);
        setFormData({
            ...row,
            operationDate: row.operationDate ? moment(row.operationDate * 1000) : null
        });
        setOpenDetail(true);
    };

    const handleCloseDetail = () => {
        setOpenDetail(false);
        setSelectedRow(null);
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const isDetailFormValid = () => {
        if (!formData) return false;
        const requiredFields = [
            'brand',
            'operationDate',
            'customerInvoiceNo',
            'customerName',
            'country',
            'price',
            'returnPrice',
            'paymentType',
            'store',
            'staff',
            'storeLocation'
        ];
        for (let field of requiredFields) {
            if (!formData[field]) return false;
            if (typeof formData[field] === 'string' && formData[field].trim() === '') return false;
        }
        return true;
    };

    const handleSaveDetail = () => {
        const formattedData = {
            operationID: formData.operationID,
            brand: formData.brand,
            customerInvoiceNo: formData.customerInvoiceNo,
            customerName: formData.customerName,
            country: formData.country,
            price: formData.price,
            returnPrice: formData.returnPrice,
            paymentType: formData.paymentType,
            store: formData.store,
            staff: formData.staff,
            storeLocation: formData.storeLocation,
            additionalNote: formData.additionalNote,
            operationDate: formData.operationDate ? formData.operationDate.format("YYYY-MM-DD") : null
        };
        setLoading(true);
        Services.UpdateOperationReport(parsedUser.token, [formattedData], "tax-free")
            .then((res) => {
                if (res.data.status) {
                    // === ÖNEMLİ: Filtreleri koruyarak listeyi yenile
                    getReports(true);
                    handleCloseDetail();
                    toast.success("Rapor başarıyla güncellendi.");
                } else {
                    console.log(res.data);
                    toast.error("Rapor güncellenirken hata oluştu.");
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    return (
        <div>
            <Stack direction="row" justifyContent="space-between" mb={2}>
                {tabs}
            </Stack>
            <Stack
                direction="row"
                justifyContent={isMobile ? "space-between" : "end"}
                mb={5}
                paddingX={2}
            >
                {isMobile ? (
                    <FilterViewButton variant="contained" onClick={handleModal} />
                ) : null}
            </Stack>

            <CustomFilter
                modalType="drawer"
                filterModal={filterModal}
                handleCloseModal={handleCloseModal}
            >
                <Stack
                    direction="column"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    paddingY={2}
                    paddingX={1}
                >
                    {TABLE_HEAD.map((column) => {
                        if (column.id === "operationDate") {
                            return (
                                <Stack key={column.id} direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label={`${column.label} Başlangıç`}
                                            format="DD/MM/YYYY"
                                            value={filters[column.id]?.start || null}
                                            onChange={(newValue) => {
                                                setFilters({
                                                    ...filters,
                                                    [column.id]: { ...(filters[column.id] || {}), start: newValue }
                                                });
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                        <DatePicker
                                            label={`${column.label} Bitiş`}
                                            format="DD/MM/YYYY"
                                            value={filters[column.id]?.end || null}
                                            onChange={(newValue) => {
                                                setFilters({
                                                    ...filters,
                                                    [column.id]: { ...(filters[column.id] || {}), end: newValue }
                                                });
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>
                                </Stack>
                            );
                        }
                        // Çoklu seçimli dropdownlar: Marka ve Lokasyon
                        else if (["brand", "poiTitle"].includes(column.id)) {
                            const options = uniqueValues(column.id);
                            return (
                                <FormControl key={column.id} fullWidth margin="normal" variant="outlined">
                                    <InputLabel id={`${column.id}-label`}>{column.label}</InputLabel>
                                    <Select
                                        labelId={`${column.id}-label`}
                                        multiple
                                        input={<OutlinedInput label={column.label} />}
                                        renderValue={(selected) => (selected || []).join(", ")}
                                        value={filters[column.id] || []}
                                        onChange={(e) => {
                                            const value = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                                            setFilters({ ...filters, [column.id]: value });
                                        }}
                                    >
                                        {options.map(opt => (
                                            <MenuItem key={opt} value={opt}>
                                                <Checkbox checked={(filters[column.id] || []).indexOf(opt) > -1} />
                                                <ListItemText primary={opt} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        }
                        // Diğer kolonlar: metin arama
                        else if (column.label) {
                            return (
                                <TextField
                                    key={column.id}
                                    label={column.label}
                                    value={filters[column.id] || ''}
                                    onChange={(e) =>
                                        setFilters({ ...filters, [column.id]: e.target.value })
                                    }
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                />
                            );
                        }
                        return null;
                    })}
                </Stack>
                <Divider sx={{ width: "100%", paddingTop: "10px" }} />
                <Stack
                    mr={2}
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    paddingY={2}
                    paddingX={1}
                >
                    <ClearButton onClick={() => clearFilters()} />
                    <FilterButton onClick={() => getFilteredTaxFreeReports()} />
                </Stack>
            </CustomFilter>

            <Card>
                {loading ? (
                    <Backdrop
                        sx={{ color: "#fff", zIndex: 2, height: "100%" }}
                        open={loading}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                ) : (
                    <>
                        <DynamicTable
                            data={reportList}
                            headers={TABLE_HEAD}
                            isOrdered={true}
                            initialOrder="operationDate"
                            initalOrderDirection="desc"
                            isMobile={isMobile}
                            handleModal={handleModal}
                            handleCloseModal={handleCloseModal}
                        />
                        {/* Detay Modal */}
                        <Dialog open={openDetail} onClose={handleCloseDetail} fullWidth maxWidth="sm">
                            <DialogTitle>Detay Düzenle</DialogTitle>
                            <DialogContent>
                                <Stack spacing={2} mt={1}>
                                    {/* Marka */}
                                    <FormControl fullWidth>
                                        <InputLabel id="brand-detail-label">Marka</InputLabel>
                                        <Select
                                            labelId="brand-detail-label"
                                            value={formData.brand || ''}
                                            label="Marka"
                                            onChange={(e) => handleFormChange('brand', e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Seçiniz</em>
                                            </MenuItem>
                                            {["GLOBAL BLUE", "JOY REFUND", "PLANET", "FREEZONE"].map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {/* İşlem Tarihi */}
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label="İşlem Tarihi"
                                            format="DD/MM/YYYY"
                                            value={formData.operationDate || null}
                                            onChange={(newValue) => handleFormChange('operationDate', newValue)}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>
                                    {/* Müşteri Fatura No (Uppercase)*/}
                                    <TextField
                                        label="Müşteri Fatura No"
                                        value={formData.customerInvoiceNo || ''}
                                        onChange={(e) => handleFormChange('customerInvoiceNo', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    {/* Müşteri Adı (Uppercase) */}
                                    <TextField
                                        label="Müşteri Adı"
                                        value={formData.customerName || ''}
                                        onChange={(e) => handleFormChange('customerName', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    {/* Ülke */}
                                    <FormControl fullWidth>
                                        <InputLabel id="country-detail-label">Ülke</InputLabel>
                                        <Select
                                            labelId="country-detail-label"
                                            value={formData.country || ''}
                                            label="Ülke"
                                            onChange={(e) => handleFormChange('country', e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Seçiniz</em>
                                            </MenuItem>
                                            {[
                                                'AFGHANISTAN','ALBANIA','ALGERIA','ANDORRA','ANGOLA','ANTIGUA AND BARBUDA','ARGENTINA','ARMENIA','AUSTRALIA','AUSTRIA','AZERBAIJAN','THE BAHAMAS','BAHRAIN','BANGLADESH','BARBADOS','BELARUS','BELGIUM','BELIZE','BENIN','BHUTAN','BOLIVIA','BOSNIA AND HERZEGOVINA','BOTSWANA','BRAZIL','BRUNEI','BULGARIA','BURKINA FASO','BURUNDI','CABO VERDE','CAMBODIA','CAMEROON','CANADA','CENTRAL AFRICAN REPUBLIC','CHAD','CHILE','CHINA','COLOMBIA','COMOROS','CONGO, DEMOCRATIC REPUBLIC OF THE','CONGO, REPUBLIC OF THE','COSTA RICA','CÔTE D’IVOIRE','CROATIA','CUBA','CYPRUS','CZECH REPUBLIC','DENMARK','DJIBOUTI','DOMINICA','DOMINICAN REPUBLIC','EAST TIMOR (TIMOR-LESTE)','ECUADOR','EGYPT','EL SALVADOR','EQUATORIAL GUINEA','ERITREA','ESTONIA','ESWATINI','ETHIOPIA','FIJI','FINLAND','FRANCE','GABON','THE GAMBIA','GEORGIA','GERMANY','GHANA','GREECE','GRENADA','GUATEMALA','GUINEA','GUINEA-BISSAU','KOSOVO','GUYANA','HAITI','HONDURAS','HUNGARY','ICELAND','INDIA','INDONESIA','IRAN','IRAQ','IRELAND','ISRAEL','ITALY','JAMAICA','JAPAN','JORDAN','KAZAKHSTAN','KENYA','KIRIBATI','KOREA, NORTH','KOREA, SOUTH','KUWAIT','KYRGYZSTAN','LAOS','LEBANON','LATVIA','LESOTHO','LIBERIA','LIBYA','LIECHTENSTEIN','LITHUANIA','LUXEMBOURG','MADAGASCAR','MALAWI','MALAYSIA','MALDIVES','MALI','MALTA','MARSHALL ISLANDS','MAURITANIA','MAURITIUS','MEXICO','MICRONESIA, FEDERATED STATES OF','MOLDOVA','MONACO','MONGOLIA','MONTENEGRO','MOROCCO','MOZAMBIQUE','MYANMAR (BURMA)','NAMIBIA','NAURU','NEPAL','NETHERLANDS','NEW ZEALAND','NICARAGUA','NIGER','NIGERIA','NORTH MACEDONIA','NORWAY','OMAN','PAKISTAN','PALAU','PANAMA','PAPUA NEW GUINEA','PARAGUAY','PERU','PHILIPPINES','POLAND','PORTUGAL','QATAR','ROMANIA','RUSSIA','RWANDA','SAINT KITTS AND NEVIS','SAINT LUCIA','SAINT VINCENT AND THE GRENADINES','SAMOA','SAN MARINO','SAO TOME AND PRINCIPE','SAUDI ARABIA','SENEGAL','SERBIA','SEYCHELLES','SIERRA LEONE','SINGAPORE','SLOVAKIA','SLOVENIA','SOLOMON ISLANDS','SOMALIA','SOUTH AFRICA','SPAIN','SRI LANKA','SUDAN','SUDAN, SOUTH','SURINAME','SWEDEN','SWITZERLAND','SYRIA','TAIWAN','TAJIKISTAN','TANZANIA','THAILAND','TOGO','TONGA','TRINIDAD AND TOBAGO','TUNISIA','TURKMENISTAN','TUVALU','UGANDA','UKRAINE','UNITED ARAB EMIRATES','UNITED KINGDOM','UNITED STATES','URUGUAY','UZBEKISTAN','VANUATU','VATICAN CITY','VENEZUELA','VIETNAM','YEMEN','ZAMBIA','ZIMBABWE',
                                            ].map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {/* Fatura Tutarı */}
                                    <TextField
                                        label="Fatura Tutarı"
                                        type="number"
                                        value={formData.price || ''}
                                        onChange={(e) => handleFormChange('price', e.target.value)}
                                        fullWidth
                                    />
                                    {/* Ödenen İade Tutarı */}
                                    <TextField
                                        label="Ödenen İade Tutarı"
                                        type="number"
                                        value={formData.returnPrice || ''}
                                        onChange={(e) => handleFormChange('returnPrice', e.target.value)}
                                        fullWidth
                                    />
                                    {/* Ödeme Türü */}
                                    <FormControl fullWidth>
                                        <InputLabel id="paymentType-detail-label">Ödeme Türü</InputLabel>
                                        <Select
                                            labelId="paymentType-detail-label"
                                            value={formData.paymentType || ''}
                                            label="Ödeme Türü"
                                            onChange={(e) => handleFormChange('paymentType', e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Seçiniz</em>
                                            </MenuItem>
                                            {["KREDİ KARTI", "NAKİT"].map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {/* Mağaza (Uppercase) */}
                                    <TextField
                                        label="Mağaza"
                                        value={formData.store || ''}
                                        onChange={(e) => handleFormChange('store', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    {/* EP Çalışanı (Uppercase) */}
                                    <TextField
                                        label="EP Çalışanı"
                                        value={formData.staff || ''}
                                        onChange={(e) => handleFormChange('staff', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    {/* Mağaza Lokasyonu (Uppercase) */}
                                    <TextField
                                        label="Mağaza Lokasyonu"
                                        value={formData.storeLocation || ''}
                                        onChange={(e) => handleFormChange('storeLocation', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    {/* Not */}
                                    <TextField
                                        label="Not"
                                        value={formData.additionalNote || ''}
                                        onChange={(e) => handleFormChange('additionalNote', e.target.value)}
                                        fullWidth
                                    />
                                </Stack>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDetail}>İptal</Button>
                                <Button
                                    onClick={handleSaveDetail}
                                    variant="contained"
                                    disabled={!isDetailFormValid()}
                                >
                                    Kaydet
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </>
                )}
            </Card>
        </div>
    );
}